import * as Sentry from '@sentry/react';
import type { BraintreeError, HostedFields, HostedFieldsTokenizePayload } from 'braintree-web';
import braintreeWebClient from 'braintree-web/client';
import braintreeWebHostedFields from 'braintree-web/hosted-fields';
import paypalCheckout from 'braintree-web/paypal-checkout';
import threeDSecure from 'braintree-web/three-d-secure';
import URI from 'jsuri';
import moment from 'moment';
import type { FlowType, Intent } from 'paypal-checkout-components/modules/callback-data';

import logger from 'js/app/loggerSingleton';
import Retracked from 'js/lib/retracked';

import {
  PAYPAL_CLIENT_ID,
  PAYPAL_CLIENT_ID_SANDBOX,
  braintreeFieldIds,
} from 'bundles/payments-common/common/constants';
import type {
  BillingAddressInput,
  BraintreePaypalResponse,
  BraintreeTokenizationOptions,
  BraintreeTokenizationResponse,
  CreditCardNonce,
  PaypalOptions,
  PaypalPaymentConfig,
  ThreeDSecureOptions,
  ThreeDSecureResponse,
  TokenizeCardRequest,
  TokenizeHostedFieldsRequest,
} from 'bundles/payments-common/common/types';
import type { BillingCycleTypesValues } from 'bundles/subscriptions-common/common/BillingCycleType';
import BillingCycleType from 'bundles/subscriptions-common/common/BillingCycleType';

import _t from 'i18n!nls/payments';

let localThreeDSecureInstance: $TSFixMe;
let verifyCardPromiseResolve: $TSFixMe;
let verifyCardPromiseReject: $TSFixMe;

let paypalPromise: $TSFixMe;

// Temporary event tracking to help debug 3DS experiment
// TODO (htran) remove after debug is done
const _trackEvent = (trackingName: string, data?: Record<string, string | boolean | undefined>) => {
  const CONTEXT_DATA = {
    namespace: {
      app: 'payments',
      page: 'page',
    },
  };
  Retracked.trackComponent(CONTEXT_DATA, data ?? {}, trackingName, 'view');
};

// All of these config props MUST be set together for either standalone or recurring payments to work
// Any other permutation of props will result in an SDK error
const getPaypalPaymentConfig = (singleUse: boolean): PaypalPaymentConfig =>
  singleUse
    ? {
        createFnProp: 'createOrder',
        intent: 'authorize' as Intent.Authorize,
        flow: 'checkout' as FlowType.Checkout,
        vault: 'false',
      }
    : {
        createFnProp: 'createBillingAgreement',
        intent: 'capture' as Intent.Capture,
        flow: 'vault' as FlowType.Vault,
        vault: 'true',
      };

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type PayPal = any;

declare global {
  interface Window {
    paypal: PayPal;
  }
}

// Reference: https://developer.paypal.com/docs/checkout/reference/customize-sdk/
const initPaypal = ({ currency, singleUse, shouldReinitialize, isSandboxEnabled }: PaypalOptions): Promise<PayPal> => {
  // Since PayPal config is set at script injection time, `shouldReinitialize` exists as a flag to reset the cached PayPal lib
  if (!paypalPromise || shouldReinitialize || isSandboxEnabled) {
    if (shouldReinitialize || isSandboxEnabled) {
      // Clear the window.paypal object to force re-initialization
      if (window.paypal) {
        delete window.paypal;
      }
    }

    const { intent, vault } = getPaypalPaymentConfig(singleUse);

    const paypalSdkUri = new URI('https://www.paypal.com/sdk/js')
      .addQueryParam('client-id', isSandboxEnabled ? PAYPAL_CLIENT_ID_SANDBOX : PAYPAL_CLIENT_ID)
      .addQueryParam('disable-funding', 'credit,card,sepa') // only show PayPal
      .addQueryParam('commit', 'false')
      .addQueryParam('currency', currency)
      .addQueryParam('intent', intent)
      .addQueryParam('vault', vault);

    paypalPromise = new Promise((resolve, reject) => {
      const s = window.document.createElement('script');
      s.src = paypalSdkUri.toString();
      s.onload = () => resolve(window.paypal);
      s.onerror = () => reject(new Error('Unable to load Paypal SDK'));
      window.document.body.appendChild(s);
    });
  }

  return paypalPromise;
};

const reportBraintreeError = (braintreeError: $TSFixMe) => {
  Sentry.captureMessage(braintreeError.message, { extra: { braintreeError } });
};

const setAttributes = (hostedFields: HostedFields) => {
  const fieldOptions = ['number', 'expirationDate', 'cvv'];
  fieldOptions.forEach((field, index) => {
    hostedFields.setAttribute(
      {
        field,
        attribute: 'aria-required',
        value: true,
      },
      function (err) {
        logger.error(err);
      }
    );
  });
};

// Even Braintree defines it as `any`:
// https://github.com/DefinitelyTyped/DefinitelyTyped/blob/752136c29dec6f1e559dfc62f6acba08f207c280/types/braintree-web/index.d.ts#L688-L689
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type HostedFieldsStyles = any;

/**
 * Conditionally creates Braintree Hosted Fields instance
 * @param {Function} getBraintreeClientToken - Function to get the Braintree client token
 * @param {Object} styles - Hosted fields styles
 * @param {HostedFields} [braintreeHostedFields] - optional existing Braintree Hosted Fields instance
 * @returns {Promise<HostedFields | void>} A promise that resolves to the Hosted Fields instance or void if no token is available
 */
export const conditionallyCreateBraintreeHostedFields = (
  getBraintreeClientToken: () => string | undefined,
  styles: HostedFieldsStyles,
  braintreeHostedFields?: HostedFields
): Promise<HostedFields | void> => {
  const braintreeClientToken = getBraintreeClientToken();
  if (!braintreeClientToken) {
    return Promise.resolve();
  }
  // First, clean up the previous fields, if we're switching from production to sandbox (or vice-versa).
  const tearDown = braintreeHostedFields ? braintreeHostedFields.teardown() : Promise.resolve();
  return (
    tearDown
      // Don't prevent creating the new fields if we have a teardown error (but still report it).
      .catch(reportBraintreeError)
      // Now create the new client and hosted fields.
      .then(() => braintreeWebClient.create({ authorization: braintreeClientToken }))
      .then((clientInstance) =>
        braintreeWebHostedFields.create({
          client: clientInstance,
          styles,
          fields: {
            number: {
              selector: `#${braintreeFieldIds.number}`,
              placeholder: _t('Enter your card number'),
            },
            expirationDate: {
              selector: `#${braintreeFieldIds.expirationDate}`,
              placeholder: _t('MM / YY'),
            },
            cvv: {
              selector: `#${braintreeFieldIds.cvv}`,
              placeholder: 'CVV',
            },
          },
        })
      )
      .then((hostedFields) => {
        // Set `aria-required` attribute to aid Web accessibility
        setAttributes(hostedFields);

        // Only return the hosted fields object if nothing's changed in the meantime, e.g. the calling component has
        // gone away, or the token has changed (as happens when switching to and from sandbox).
        if (braintreeClientToken === getBraintreeClientToken()) {
          return hostedFields;
        }

        Sentry.captureMessage('Braintree client token changed after hosted fields were instantiated', {
          level: 'warning',
        });
        return undefined;
      })
      .catch(reportBraintreeError)
  );
};

function billingAddress(tokenizeRequest: BillingAddressInput) {
  const { billingZipcode, billingCountry } = tokenizeRequest;

  const US_COUNTRY_CODE = 'US';

  if (billingCountry === US_COUNTRY_CODE && billingZipcode) {
    return {
      billingAddress: {
        postalCode: billingZipcode,
      },
    };
  } else if (billingCountry && billingCountry !== US_COUNTRY_CODE) {
    return {
      billingAddress: {
        countryCodeAlpha2: billingCountry,
      },
    };
  } else {
    return {};
  }
}

/**
 * Tokenize card
 * @param {Object} tokenizeCardRequest - tokenizeCardRequest
 * @param {string} clientToken - braintreeClientToken
 * @returns {Promise<CreditCardNonce>} A promise that resolves to the credit card nonce
 * @see https://developers.braintreepayments.com/guides/client-sdk/setup/javascript/v3
 */
export const tokenizeCard = (
  tokenizeCardRequest: TokenizeCardRequest,
  clientToken: string
): Promise<CreditCardNonce> => {
  const { number, cardholderName, expirationMonth, expirationYear, cvv } = tokenizeCardRequest;

  const requestBody: BraintreeTokenizationOptions = {
    cardholderName,
    number,
    expirationMonth,
    expirationYear,
    cvv,
    ...billingAddress(tokenizeCardRequest),
  };

  return new Promise((resolve, reject) => {
    braintreeWebClient
      .create({
        authorization: clientToken,
      })
      .then((clientInstance) => {
        clientInstance.request(
          {
            endpoint: 'payment_methods/credit_cards',
            method: 'post',
            data: { creditCard: requestBody },
          },
          (err: BraintreeError | undefined, res: BraintreeTokenizationResponse) => {
            if (err) {
              Sentry.setExtras({
                braintreeError: err,
              });
              reject(err.details.originalError.error.message);
            } else {
              // @ts-expect-error TS2345 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
              resolve(res.creditCards[0]);
            }
          }
        );
      });
  });
};

/**
 * Tokenizes credit card data using Braintree's Hosted Fields integration.
 *
 * @param {TokenizeHostedFieldsRequest} tokenizeHostedFieldsRequest - Request object containing cardholder name and billing information
 * @param {HostedFields} braintreeHostedFields - Braintree Hosted Fields instance
 * @returns {Promise<HostedFieldsTokenizePayload>} A promise that resolves to the tokenization payload
 * @see https://developers.braintreepayments.com/guides/hosted-fields/overview/javascript/v3
 */
export const tokenizeHostedFields = (
  tokenizeHostedFieldsRequest: TokenizeHostedFieldsRequest,
  braintreeHostedFields: HostedFields
): Promise<HostedFieldsTokenizePayload> => {
  const { cardholderName } = tokenizeHostedFieldsRequest;

  return braintreeHostedFields
    .tokenize({
      cardholderName,
      ...billingAddress(tokenizeHostedFieldsRequest),
    })
    .catch((braintreeError) => {
      Sentry.setExtras({
        braintreeError,
      });
      // rethrow (similar to "reject(err.details.originalError.error.message)" above)
      throw braintreeError.message;
    });
};

// Reference: https://braintree.github.io/braintree-web/3.44.0/PayPalCheckout.html
export const setupPaypal = (clientToken: string, paypalOptions: PaypalOptions): Promise<void> => {
  return initPaypal(paypalOptions).then((paypal) => {
    return braintreeWebClient
      .create({
        authorization: clientToken,
      })
      .then((clientInstance) => {
        return paypalCheckout.create({
          client: clientInstance,
        });
      })
      .then((paypalCheckoutInstance) => {
        const { singleUse, amount, currency, onSuccess, onError, onCancel, style = {} } = paypalOptions;
        const { createFnProp, intent, flow } = getPaypalPaymentConfig(singleUse);

        return (
          paypal
            // eslint-disable-next-line new-cap
            .Buttons({
              style: {
                color: 'blue',
                label: 'paypal',
                ...style,
              },
              [createFnProp]: () => {
                return paypalCheckoutInstance.createPayment({
                  intent,
                  flow,
                  amount,
                  currency,
                });
              },
              onApprove: (data: $TSFixMe) => {
                return paypalCheckoutInstance
                  .tokenizePayment(data)
                  .then((res: BraintreePaypalResponse) => onSuccess(res))
                  .catch((err: BraintreeError) => {
                    Sentry.setExtras({
                      braintreeError: err,
                    });
                    return onError(err);
                  });
              },
              onError,
              onCancel,
            })
            .render(`#${paypalOptions.containerId}`)
        );
      });
  });
};

const verifyCardThreeDSecureInstance = (threeDSecureInstance: $TSFixMe, options: ThreeDSecureOptions) => {
  const { amount, nonce, bin } = options;
  return threeDSecureInstance.verifyCard({
    amount,
    nonce,
    bin,
    onLookupComplete: (data: $TSFixMe, next: $TSFixMe) => {
      next();
    },
  });
};

/**
 * 3D Secure for card
 * @param {string} clientToken - braintreeClientToken
 * @param {Object} options - threeDSecureRequest
 * @returns {Promise<ThreeDSecureResponse>} A promise that resolves to the 3D Secure response
 * @see https://developers.braintreepayments.com/guides/3d-secure/client-side/javascript/v3
 */
export const threeDSecureVerifyCard = (
  clientToken: string,
  options: ThreeDSecureOptions
): Promise<ThreeDSecureResponse> => {
  _trackEvent('three_d_secure_attempt');

  return braintreeWebClient
    .create({
      authorization: clientToken,
    })
    .then((clientInstance) => {
      return threeDSecure.create({
        client: clientInstance,
        version: 2,
      });
    })
    .then((threeDSecureInstance) => {
      localThreeDSecureInstance = threeDSecureInstance;

      // Updates Sentry log with searchable, indexed tag to get better insights on 3DS failures
      Sentry.setTags({ threeDSecureVerificationAttempted: 'true' });

      return new Promise((resolve, reject) => {
        verifyCardPromiseResolve = resolve;
        verifyCardPromiseReject = reject;
        verifyCardThreeDSecureInstance(threeDSecureInstance, options)
          .then((response: ThreeDSecureResponse) => {
            const { liabilityShifted, liabilityShiftPossible, threeDSecureInfo } = response;
            const trackingData = {
              liabilityShifted,
              liabilityShiftPossible,
              status: threeDSecureInfo?.status,
            };

            // liabilityShiftPossible == false && liabilityShifted == false - 3DS not available, continue checkout
            // liabilityShiftPossible == true && liabilityShifted == true - 3DS available and authenticated, continue checkout
            // liabilityShiftPossible == false && liabilityShifted == true - invalid response, should not be possible
            // liabilityShiftPossible == true && liabilityShifted == false - 3DS available and not authenticated, show error
            // For more details about `liabilityShiftPossible` and `liabilityShifted`, see the 3DS JS reference:
            // https://developers.braintreepayments.com/guides/3d-secure/client-side/javascript/v3
            if ((!liabilityShiftPossible && liabilityShifted) || (liabilityShiftPossible && !liabilityShifted)) {
              _trackEvent('three_d_secure_failure', trackingData);

              // Treat as an error, which is handled in `catch()` block
              return Promise.reject(response);
            }

            _trackEvent('three_d_secure_successful_or_bypassed', trackingData);

            return resolve(response);
          })
          .catch((err: BraintreeError) => {
            _trackEvent('three_d_secure_error');

            Sentry.setExtras({
              braintreeError: err,
            });
            reject(err);
          })
          // TODO (htran) figure out why only `then()` works and `finally()` doesn't
          .then(() => {
            // Remove locally scoped threeDSecure instance now that the wrapper promise is resolved / rejected
            localThreeDSecureInstance = null;
            verifyCardPromiseReject = null;
            verifyCardPromiseResolve = null;
          });
      });
    });
};

// Reference: https://braintree.github.io/braintree-web/3.44.0/ThreeDSecure.html#cancelVerifyCard
// Makes the assumption that there will only be one threeDSecureInstance at any given time
// TODO (htran): update the implementation of this if this assumption is no longer true
export const cancelThreeDSecureVerifyCard = (): void => {
  if (localThreeDSecureInstance) {
    localThreeDSecureInstance.cancelVerifyCard((err: $TSFixMe, payload: $TSFixMe) => {
      if (typeof verifyCardPromiseReject === 'function' && typeof verifyCardPromiseResolve === 'function') {
        if (err || !payload) {
          verifyCardPromiseReject(err);
        } else {
          verifyCardPromiseResolve(payload);
        }
      }

      localThreeDSecureInstance = null;
      verifyCardPromiseReject = null;
      verifyCardPromiseResolve = null;
    });
  }
};

// Reference: https://developers.braintreepayments.com/reference/request/subscription/create/java#billing_day_of_month
// BT Support confirmed the following behavior and that the logic would have to be implemented manually:
// Ex. Checkout Jan 31st, Renew Feb 28th, Renew Mar 31st, Renew Apr 30th
// Ex. Checkout Jan 30th, Renew Feb 28th, Renew Mar 30st, Renew Apr 30th
// Ex. Checkout Jan 29th, Renew Feb 28th, Renew Mar 29th, Renew Apr 29th
// Ex. Checkout Jan 28th, Renew Feb 28th, Renew Mar 28th, Renew Apr 28th
export const getNextBillingTimestampFromToday = (
  billingCycle: BillingCycleTypesValues,
  promotionBillingCycle?: number
): number => {
  const durationAmount = promotionBillingCycle || 1;
  let incrementKey;

  if (billingCycle === BillingCycleType.MONTHLY) {
    incrementKey = 'month';
  } else if (billingCycle === BillingCycleType.ANNUAL) {
    incrementKey = 'year';
  } else {
    throw new Error(`Cannot determine next billing timestamp with billing cycle ${billingCycle}`);
  }
  // Moment automatically goes to end of month instead of using invalid date for case of Jan 31 -> Feb 28
  return Number(
    moment()
      .add(durationAmount, incrementKey as moment.DurationInputArg2)
      .format('x')
  );
};

export default {
  tokenizeCard,
  tokenizeHostedFields,
  setupPaypal,
  threeDSecureVerifyCard,
  cancelThreeDSecureVerifyCard,
  getNextBillingTimestampFromToday,
};
